import { useMemo, useState } from "react";
import { Search, MessageCircleQuestion } from "lucide-react";

const SYNONYMS = {
  tub: ["spa"],
  hot: ["spa"],
  wifi: ["internet", "password", "network"],
  internet: ["wifi", "password"],
  password: ["wifi", "internet"],
  spa: ["hot tub", "hottub", "jacuzzi"],
  hottub: ["spa", "hot tub"],
  jacuzzi: ["spa", "hot tub"],
  trash: ["garbage", "bins", "rubbish"],
  garbage: ["trash", "bins"],
  remote: ["tv", "television", "streaming"],
  tv: ["remote", "television"],
  television: ["tv", "remote"],
  dinner: ["food", "restaurants", "eat", "restaurant"],
  food: ["dinner", "restaurants", "eat", "lunch", "breakfast", "restaurant"],
  restaurants: ["food", "dinner", "eat"],
  restaurant: ["food", "dinner", "eat"],
  eat: ["food", "dinner", "restaurants"],
  wedding: ["event", "gathering", "party", "celebration"],
  event: ["wedding", "gathering", "party"],
  gathering: ["wedding", "event", "party"],
  party: ["event", "gathering"],
  swim: ["pool", "swimming"],
  pool: ["swim", "swimming", "waterfall"],
  checkout: ["leave", "departure", "leaving", "check out"],
  leave: ["checkout", "departure"],
  fire: ["firepit", "fire pit", "bonfire", "campfire"],
  bonfire: ["fire pit", "firepit"],
  grill: ["bbq", "barbecue", "propane"],
  bbq: ["grill", "barbecue"],
  chicken: ["chickens", "eggs", "hens"],
  eggs: ["chickens", "chicken"],
  gate: ["entry", "arrival", "entrance"],
  ac: ["thermostat", "air conditioning", "climate", "temperature"],
  heat: ["thermostat", "climate", "heating"],
  thermostat: ["ac", "temperature", "climate"],
  towels: ["towel", "linens"],
  towel: ["towels", "linens"],
  boat: ["kayak", "rental", "rentals", "marina"],
  kayak: ["boat", "rental", "rentals"],
  lost: ["lost and found", "forgot", "left behind"],
};

const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

export const buildSearchIndex = (content) => {
  const items = [];
  for (const section of content.sections) {
    for (const panel of section.panels) {
      const text = [panel.title, ...(panel.body || []), ...(panel.bullets || [])].join(" ");
      items.push({ id: panel.id, section: section.title, title: panel.title, text });
    }
  }
  for (const cat of content.local_guide.categories) {
    for (const item of cat.items) {
      items.push({
        id: `local-${cat.id}`,
        section: "Local guide",
        title: item.name,
        text: `${item.name} ${item.desc} ${item.note || ""} ${cat.title}`,
      });
    }
  }
  return items;
};

export const SearchBox = ({ index, onSelect, onAskConcierge }) => {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const results = useMemo(() => {
    const q = normalize(query);
    if (q.length < 2) return [];
    const terms = q.split(" ");
    const expanded = new Set(terms);
    for (const t of terms) {
      const compact = t.replace(/\s/g, "");
      for (const syn of SYNONYMS[compact] || []) syn.split(" ").forEach((s) => expanded.add(s));
    }
    return index
      .map((item) => {
        const hayTokens = new Set(normalize(item.text).split(" "));
        const titleTokens = new Set(normalize(item.title).split(" "));
        let score = 0;
        for (const term of expanded) {
          const inTitle = titleTokens.has(term) || [...titleTokens].some((tok) => term.length >= 4 && tok.startsWith(term));
          const inBody = hayTokens.has(term) || [...hayTokens].some((tok) => term.length >= 4 && tok.startsWith(term));
          if (inTitle) score += 5;
          else if (inBody) score += 2;
        }
        if (item.id.startsWith("local-")) score -= 1;
        return { item, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 7);
  }, [query, index]);

  const snippet = (text) => {
    const q = normalize(query).split(" ")[0];
    const idx = normalize(text).indexOf(q);
    if (idx < 0) return text.slice(0, 110) + (text.length > 110 ? "…" : "");
    const start = Math.max(0, idx - 40);
    return (start > 0 ? "…" : "") + text.slice(start, idx + 90) + (text.length > idx + 90 ? "…" : "");
  };

  const open = query.trim().length >= 2 && focused;

  return (
    <div className="relative w-full max-w-2xl mx-auto" data-testid="guide-search">
      <div className="flex items-center gap-3 bg-white border border-charcoal/15 rounded-full px-5 py-4 shadow-sm focus-within:border-copper focus-within:shadow-md transition-all">
        <Search size={20} className="text-soft shrink-0" aria-hidden />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          placeholder="Search the guide — try “Wi-Fi”, “hot tub”, “trash”…"
          className="flex-1 bg-transparent outline-none text-base text-charcoal placeholder:text-soft/70"
          aria-label="Search the guest guide"
          data-testid="guide-search-input"
        />
        {query && (
          <button onClick={() => setQuery("")} className="text-soft text-sm font-semibold hover:text-charcoal" data-testid="guide-search-clear" aria-label="Clear search">
            Clear
          </button>
        )}
      </div>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-charcoal/10 shadow-xl overflow-hidden z-40" data-testid="guide-search-results">
          {results.length === 0 ? (
            <div className="p-5 text-center" data-testid="guide-search-no-results">
              <p className="text-soft mb-3">No matches in the guide for “{query}”.</p>
              <button
                onMouseDown={(e) => { e.preventDefault(); onAskConcierge(query); }}
                className="inline-flex items-center gap-2 bg-copper text-cream rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-copperdeep transition-colors"
                data-testid="search-ask-concierge-button"
              >
                <MessageCircleQuestion size={16} aria-hidden /> Ask the concierge instead
              </button>
            </div>
          ) : (
            <ul>
              {results.map(({ item }) => (
                <li key={`${item.id}-${item.title}`}>
                  <button
                    onMouseDown={(e) => { e.preventDefault(); onSelect(item.id); setQuery(""); }}
                    className="w-full text-left px-5 py-3.5 hover:bg-cream transition-colors border-b border-charcoal/5 last:border-0"
                    data-testid={`search-result-${item.id}`}
                  >
                    <span className="block text-[11px] uppercase tracking-[0.14em] text-sagedeep font-semibold">{item.section}</span>
                    <span className="block font-semibold text-charcoal">{item.title}</span>
                    <span className="block text-sm text-soft mt-0.5">{snippet(item.text)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
